import { adSlotSizes } from "@config/AdsConstants";
import { ADS_DOMAIN } from "@helper/getEnvVariables";
import adblockDetectorScript from "@hooks/useLoadedTags/scripts/adblockDetector";
import adsCheckerScript from "@hooks/useLoadedTags/scripts/adsChecker";
import betteradsHead2Script from "@hooks/useLoadedTags/scripts/betteradsHead2Script";
import betteradsHeadScript from "@hooks/useLoadedTags/scripts/betteradsHeadScript";
import betteradsScript from "@hooks/useLoadedTags/scripts/betteradsScript";
import useScript from "@hooks/useScript/useScript";
import useScriptFunction from "@hooks/useScriptFunction/useScriptFunction";
import { useEffect, useState } from "react";

export default function useLoadedTags() {
  const [isPrestitialEnabled, setIsPrestitialEnabled] = useState<
    boolean | undefined
  >(undefined);
  const [isCatfishEnabled, setIsCatfishEnabled] = useState<boolean | undefined>(
    undefined,
  );
  const [isTaboolaEnabled, setIsTaboolaEnabled] = useState<boolean | undefined>(
    undefined,
  );

  useScript({
    id: "ads-checker-script",
    src: ADS_DOMAIN + "/tag/ads/ads_checker.js",
    type: "text/javascript",
  });

  useScript({
    id: "suid-script",
    src: "https://cdn.sphlabs.com/suid/suid.min.js",
    type: "text/javascript",
  });

  useScriptFunction({
    id: "ads-checker-custom-script",
    value: adsCheckerScript,
    portion: "head",
  });

  useScriptFunction({
    id: "ad-block-detector-script",
    value: adblockDetectorScript,
    portion: "head",
  });

  useEffect(() => {
    // Make sure that googletag.cmd exists.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unnecessary-condition
    window.googletag = (window.googletag as typeof googletag | undefined) || {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unnecessary-condition
    ((window.googletag as typeof googletag).cmd as googletag.CommandArray) =
      (window.googletag as typeof googletag).cmd || []; // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
    window.googletag.slots = window.googletag.slots || {};

    // Correct: Queueing the callback on the command queue.
    googletag.cmd.push(function () {
      const dfpTagsObj = googletag
        .pubads()
        .getSlots()
        .map((_slot) => {
          const id = _slot.getSlotElementId().replaceAll("dfp-ad-", "");

          const sizeId: string = id.startsWith("lb") ? "lb1" : id;

          const size = Object.keys(adSlotSizes[sizeId]).map((_, index) => {
            const sizes = adSlotSizes[sizeId][index];

            return sizes.toString().replaceAll(",", "x");
          });

          const loadedDfpTag = {
            [id]: {
              adunit: _slot.getAdUnitPath(),
              slotname: _slot.getSlotElementId(),
              size: size.join(","),
            },
          };

          // Added googletag.slots so ads team can feed from it.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          window.googletag.slots[
            _slot.getSlotElementId().replaceAll("dfp-ad-", "")
          ] = _slot;

          return loadedDfpTag;
        });

      const loadedDfpTags: Record<string, object> = {};

      dfpTagsObj.forEach((item: Record<string, object>) => {
        const key = Object.keys(item).at(0);

        if (key) {
          loadedDfpTags[key] = item[key];
        }
      });

      // Load dfpTagsScript.
      const loadDfpTagsScriptId = "loadDfpTagsScript";
      const loadDfpTagsScriptComponent: HTMLElement | null =
        document.getElementById(loadDfpTagsScriptId);

      if (!loadDfpTagsScriptComponent) {
        const loadDfpTagsScript = document.createElement("script");

        loadDfpTagsScript.id = loadDfpTagsScriptId;
        loadDfpTagsScript.dataset.testid = loadDfpTagsScriptId;
        loadDfpTagsScript.innerHTML = `var loaded_dfp_tags = '${JSON.stringify(
          loadedDfpTags,
        )}';`;
        loadDfpTagsScript.type = "text/javascript";
        document.body.appendChild(loadDfpTagsScript);
      }

      // Load betteradsHeadScript.
      const loadBetteradsHeadScriptId = "betteradsHeadScript";

      if (!document.getElementById(loadBetteradsHeadScriptId)) {
        const loadBetteradsHeadScript = document.createElement("script");

        loadBetteradsHeadScript.id = loadBetteradsHeadScriptId;
        loadBetteradsHeadScript.dataset.testid = loadBetteradsHeadScriptId;
        loadBetteradsHeadScript.innerHTML = betteradsHeadScript;
        loadBetteradsHeadScript.type = "text/javascript";
        document.body.appendChild(loadBetteradsHeadScript);
      }

      // Load smx_prebid.js
      const loadSmxPrebidScriptId = "loadSmxPrebidScript";

      if (!document.getElementById(loadSmxPrebidScriptId)) {
        const loadSmxPrebidScript = document.createElement("script");

        loadSmxPrebidScript.id = loadSmxPrebidScriptId;
        loadSmxPrebidScript.dataset.testid = loadSmxPrebidScriptId;
        // loadSmxPrebidScript.src = ADS_DOMAIN + "/tag/smx/smx_prebid.js";
        loadSmxPrebidScript.src =
          ADS_DOMAIN + "/tag/smx/sites/nonhb/smx_prebid.js";
        loadSmxPrebidScript.onload = () => {
          if (typeof window === "undefined") return;
          setIsPrestitialEnabled(window.prestitial_enabled === 1);
          setIsCatfishEnabled(window.catfish_enabled === 1);
          setIsTaboolaEnabled(window.taboola_enable === 1);
        };
        document.body.appendChild(loadSmxPrebidScript);
      }

      // Load betteradsScript.
      const loadBetteradsScriptId = "betteradsScript";

      if (!document.getElementById(loadBetteradsScriptId)) {
        const loadBetteradsScript = document.createElement("script");

        loadBetteradsScript.id = loadBetteradsScriptId;
        loadBetteradsScript.dataset.testid = loadBetteradsScriptId;
        loadBetteradsScript.innerHTML = betteradsScript;
        loadBetteradsScript.type = "text/javascript";
        document.body.appendChild(loadBetteradsScript);
      }

      // Load betteradsHead2Script.
      const loadBetteradsHead2ScriptId = "betteradsHead2Script";
      if (!document.getElementById(loadBetteradsHead2ScriptId)) {
        const loadBetteradsHead2Script = document.createElement("script");

        loadBetteradsHead2Script.id = loadBetteradsHead2ScriptId;
        loadBetteradsHead2Script.dataset.testid = loadBetteradsHead2ScriptId;
        loadBetteradsHead2Script.innerHTML = betteradsHead2Script;
        loadBetteradsHead2Script.type = "text/javascript";
        document.body.appendChild(loadBetteradsHead2Script);
      }
    });
  }, []);

  return { isPrestitialEnabled, isCatfishEnabled, isTaboolaEnabled };
}
